import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useAccountSummary, useAccounts } from "../hooks/accounts";
import type { AccountListItem, LiquidityCategory, Sector } from "../lib/accounts";

const NF_EUR = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" });
function fmtEur(cents: number): string {
  return NF_EUR.format(cents / 100);
}

const STALE_DAYS = 60;
function daysSince(dateStr: string | null | undefined): number | null {
  if (!dateStr) return null;
  const then = new Date(dateStr).getTime();
  if (Number.isNaN(then)) return null;
  return Math.floor((Date.now() - then) / 86_400_000);
}
function fmtRelativeDays(days: number, t: (k: string, o?: Record<string, unknown>) => string): string {
  if (days <= 0) return t("accounts.today", { defaultValue: "heute" });
  if (days === 1) return t("accounts.yesterday", { defaultValue: "gestern" });
  if (days < 30) return t("accounts.days_ago", { defaultValue: "vor {{n}} Tagen", n: days });
  if (days < 365) {
    const months = Math.floor(days / 30);
    return t("accounts.months_ago", { defaultValue: "vor {{n}} Monaten", n: months });
  }
  const years = (days / 365).toFixed(1).replace(".", ",");
  return t("accounts.years_ago", { defaultValue: "vor {{n}} Jahren", n: years });
}

const AVATAR_COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#ec4899", "#a855f7", "#0ea5e9", "#ef4444"];
function colorForKey(key: string): string {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length] ?? "#3b82f6";
}

export function AccountsListPage() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<"true" | "false" | "all">("true");
  const [sectorFilter, setSectorFilter] = useState<Sector | "ALL">("ALL");
  const [liquidityFilter, setLiquidityFilter] = useState<LiquidityCategory | "ALL">("ALL");
  const [search, setSearch] = useState("");

  const filters: {
    active?: "true" | "false";
    sector?: Sector;
    liquidityCategory?: LiquidityCategory;
  } = {};
  if (status !== "all") filters.active = status;
  if (sectorFilter !== "ALL") filters.sector = sectorFilter;
  if (liquidityFilter !== "ALL") filters.liquidityCategory = liquidityFilter;

  const list = useAccounts(filters);
  const summary = useAccountSummary();
  // Always-counts for chips (regardless of filter)
  const all = useAccounts({});
  const allAccounts = all.data?.accounts ?? [];
  const activeCount = allAccounts.filter((a) => a.isActive).length;
  const inactiveCount = allAccounts.length - activeCount;

  const filteredAccounts = useMemo(() => {
    const term = search.trim().toLowerCase();
    const acc = list.data?.accounts ?? [];
    if (!term) return acc;
    return acc.filter(
      (a) =>
        a.name.toLowerCase().includes(term) ||
        (a.institute ?? "").toLowerCase().includes(term),
    );
  }, [list.data, search]);

  const grouped = useMemo(() => {
    const m = new Map<string, AccountListItem[]>();
    for (const a of filteredAccounts) {
      const arr = m.get(a.type) ?? [];
      arr.push(a);
      m.set(a.type, arr);
    }
    for (const arr of m.values()) arr.sort((a, b) => a.name.localeCompare(b.name));
    return [...m.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [filteredAccounts]);

  return (
    <section className="space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div
            className="text-xs font-semibold uppercase tracking-wider mb-1"
            style={{ color: "var(--muted)" }}
          >
            {t("nav.group_assets")}
          </div>
          <h1 className="text-2xl font-bold tracking-tight">{t("accounts.list.title")}</h1>
          <div className="text-sm mt-1" style={{ color: "var(--muted)" }}>
            {t("accounts.list.subtitle")}
          </div>
        </div>
        <Link
          to="/accounts/new"
          className="px-3 py-2 rounded-lg text-white text-sm font-medium"
          style={{ background: "var(--accent)" }}
        >
          + {t("accounts.list.new")}
        </Link>
      </div>

      {/* Stats Strip */}
      <div
        className="fv-card grid grid-cols-2 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x"
        style={{ borderColor: "var(--border)" }}
      >
        <StatMini
          label={t("dashboard.tiles.liquide")}
          totalCents={summary.data?.byLiquidity.LIQUID?.totalCents ?? 0}
          loading={summary.isLoading}
        />
        <StatMini
          label={t("accounts.list.summary.anlagen")}
          totalCents={summary.data?.byLiquidity.ANLAGE?.totalCents ?? 0}
          loading={summary.isLoading}
          hint={t("accounts.anlagen_hint", {
            defaultValue: "Werte real im Investments-Tab",
          })}
        />
        <StatMini
          label={t("dashboard.tiles.verbindlichkeiten")}
          totalCents={summary.data?.byLiquidity.VERBINDLICHKEIT?.totalCents ?? 0}
          loading={summary.isLoading}
        />
        <StatMini
          label={t("accounts.list.summary.total")}
          totalCents={summary.data?.totalCents ?? 0}
          loading={summary.isLoading}
          emphasis
        />
      </div>

      {/* Filter row */}
      <div className="flex flex-wrap items-center gap-3">
        <div
          className="flex gap-1 p-1 rounded-lg"
          style={{ background: "var(--card)", border: "1px solid var(--border)" }}
        >
          <FilterChip
            active={status === "true"}
            onClick={() => setStatus("true")}
            label={`${t("accounts.list.filter.active")} (${activeCount})`}
          />
          <FilterChip
            active={status === "false"}
            onClick={() => setStatus("false")}
            label={`${t("accounts.list.filter.inactive")} (${inactiveCount})`}
          />
          <FilterChip
            active={status === "all"}
            onClick={() => setStatus("all")}
            label={t("accounts.list.filter.all")}
          />
        </div>
        <FilterDivider />
        <FilterLabel>{t("accounts.list.filter.sector")}</FilterLabel>
        <FilterSelect
          value={sectorFilter}
          onChange={(v) => setSectorFilter(v as typeof sectorFilter)}
          options={[
            { value: "ALL", label: t("accounts.list.filter.all") },
            { value: "HAUSHALT", label: t("accounts.sector.HAUSHALT") },
            { value: "IMMOBILIE", label: t("accounts.sector.IMMOBILIE") },
            { value: "UNZUGEORDNET", label: t("accounts.sector.UNZUGEORDNET") },
          ]}
        />
        <FilterLabel>{t("accounts.list.filter.liquidity")}</FilterLabel>
        <FilterSelect
          value={liquidityFilter}
          onChange={(v) => setLiquidityFilter(v as typeof liquidityFilter)}
          options={[
            { value: "ALL", label: t("accounts.list.filter.all") },
            { value: "LIQUID", label: t("accounts.liquidity.LIQUID") },
            { value: "ANLAGE", label: t("accounts.liquidity.ANLAGE") },
            { value: "VERBINDLICHKEIT", label: t("accounts.liquidity.VERBINDLICHKEIT") },
          ]}
        />
        <div className="flex-1" />
        <input
          type="text"
          placeholder={t("common.search", { defaultValue: "Suchen…" })}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="text-sm px-3 py-1.5 rounded-lg w-44"
          style={{
            background: "var(--card)",
            border: "1px solid var(--border)",
            color: "var(--text)",
          }}
        />
      </div>

      {/* Empty / Loading */}
      {list.isLoading ? (
        <div className="fv-card p-10 text-center" style={{ color: "var(--muted)" }}>
          …
        </div>
      ) : filteredAccounts.length === 0 ? (
        <div className="fv-card p-10 text-center">
          <p style={{ color: "var(--text)" }} className="mb-4">
            {t("accounts.list.empty")}
          </p>
          <Link
            to="/accounts/new"
            className="inline-block px-3 py-2 rounded-lg text-white text-sm font-medium"
            style={{ background: "var(--accent)" }}
          >
            {t("accounts.list.empty_cta")}
          </Link>
        </div>
      ) : (
        <>
          {grouped.map(([type, items]) => (
            <AccountGroupCard key={type} type={type} items={items} />
          ))}
          <div
            className="fv-card p-4 flex items-start gap-3"
            style={{ background: "var(--border-soft)" }}
          >
            <svg
              className="w-5 h-5 mt-0.5 shrink-0"
              style={{ color: "var(--accent)" }}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M12 16v-4M12 8h.01" />
            </svg>
            <div className="text-xs" style={{ color: "var(--muted)" }}>
              <b style={{ color: "var(--text)" }}>
                {t("accounts.info_anlage_title", {
                  defaultValue: "Warum stehen Depot- und P2P-Konten auf 0 €?",
                })}
              </b>{" "}
              {t("accounts.info_anlage_body", {
                defaultValue:
                  "Anlage-Konten tracken nur den Bestand, nicht den Marktwert. Der echte Investment-Wert läuft über die Investments-Seite mit Live-Marktdaten. Diese Konten sind hier nur zur Zuordnung von Transaktionen.",
              })}
            </div>
          </div>
        </>
      )}
    </section>
  );
}

// ============== Stats Strip ==============
function StatMini({
  label,
  totalCents,
  loading,
  emphasis,
  hint,
}: {
  label: string;
  totalCents: number;
  loading: boolean;
  emphasis?: boolean;
  hint?: string;
}) {
  return (
    <div className="p-4" style={{ borderColor: "var(--border)" }}>
      <div className="fv-kpi-label mb-1" title={hint}>
        {label}
        {hint && <span style={{ color: "var(--subtle)" }}> ⓘ</span>}
      </div>
      <div
        className={`${emphasis ? "text-2xl" : "text-xl"} font-bold tracking-tight num`}
        style={{ color: loading ? "var(--muted)" : "var(--text)" }}
      >
        {loading ? "…" : fmtEur(totalCents)}
      </div>
    </div>
  );
}

// ============== Filter helpers ==============
function FilterChip({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="px-3 py-1.5 rounded-md text-xs font-medium transition-colors"
      style={
        active
          ? { background: "var(--text)", color: "var(--bg)" }
          : { color: "var(--muted)", background: "transparent" }
      }
    >
      {label}
    </button>
  );
}
function FilterDivider() {
  return <div className="w-px h-6" style={{ background: "var(--border)" }} />;
}
function FilterLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-xs" style={{ color: "var(--muted)" }}>
      {children}
    </span>
  );
}
function FilterSelect({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="text-sm px-2 py-1.5 rounded-lg"
      style={{
        background: "var(--card)",
        border: "1px solid var(--border)",
        color: "var(--text)",
      }}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

// ============== Group Card ==============
function AccountGroupCard({
  type,
  items,
}: {
  type: string;
  items: AccountListItem[];
}) {
  const { t } = useTranslation();
  const subtotal = items.reduce(
    (acc, x) => acc + (x.latestBalance?.balanceCents ?? 0),
    0,
  );
  const isAnlageOnly = items.every((a) => a.liquidityCategory === "ANLAGE");

  return (
    <div className="fv-card overflow-hidden">
      <div
        className="px-5 py-4 flex items-center gap-4 border-b"
        style={{ borderColor: "var(--border)" }}
      >
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold"
          style={{ background: colorForKey(type) }}
          aria-hidden="true"
        >
          {t(`accounts.types.${type}`).charAt(0)}
        </div>
        <div className="min-w-0">
          <h3 className="text-base font-semibold">{t(`accounts.types.${type}`)}</h3>
          <div className="text-xs mt-0.5" style={{ color: "var(--muted)" }}>
            {items.length}{" "}
            {items.length === 1
              ? t("accounts.account_singular", { defaultValue: "Konto" })
              : t("accounts.account_plural", { defaultValue: "Konten" })}
            {isAnlageOnly &&
              ` · ${t("accounts.anlage_only_hint", { defaultValue: "Wert via Investments-Tab" })}`}
          </div>
        </div>
        <div className="flex-1" />
        <div className="text-right">
          <div className="fv-kpi-label">
            {t("accounts.list.summary.subtotal", { defaultValue: "Summe" })}
          </div>
          <div className="text-lg font-bold tracking-tight num">{fmtEur(subtotal)}</div>
        </div>
      </div>

      <div>
        {items.map((a) => (
          <AccountRow key={a.id} account={a} />
        ))}
      </div>
    </div>
  );
}

// ============== Row ==============
function AccountRow({ account }: { account: AccountListItem }) {
  const { t } = useTranslation();
  const days = daysSince(account.latestBalance?.date);
  const isStale = days !== null && days >= STALE_DAYS;
  const initial = account.name.charAt(0).toUpperCase();
  const balanceCents = account.latestBalance?.balanceCents ?? 0;
  const isZero = balanceCents === 0;

  return (
    <Link
      to={`/accounts/${account.id}`}
      className="grid grid-cols-12 gap-3 items-center px-5 py-3 transition-colors hover:bg-[var(--row-hover)] border-b last:border-b-0"
      style={{ borderColor: "var(--border-soft)" }}
    >
      <div className="col-span-12 md:col-span-4 flex items-center gap-3 min-w-0">
        <div
          className="w-8 h-8 rounded-md flex items-center justify-center text-white text-xs font-bold shrink-0"
          style={{ background: colorForKey(account.id) }}
          aria-hidden="true"
        >
          {initial}
        </div>
        <div className="min-w-0">
          <div className="font-medium truncate" style={{ color: "var(--accent)" }}>
            {account.name}
          </div>
          <div className="text-xs truncate" style={{ color: "var(--muted)" }}>
            {account.institute ?? "—"}
          </div>
        </div>
      </div>

      <div className="col-span-6 md:col-span-2 flex items-center gap-2 text-xs flex-wrap">
        <Badge tone={liquidityTone(account.liquidityCategory)}>
          {t(`accounts.liquidity.${account.liquidityCategory}`)}
        </Badge>
      </div>

      <div className="col-span-6 md:col-span-2 text-xs" style={{ color: "var(--muted)" }}>
        {t(`accounts.sector.${account.sector}`)}
      </div>

      <div className="col-span-6 md:col-span-2 text-right num font-semibold">
        <span style={{ color: isZero ? "var(--muted)" : "var(--text)" }}>
          {fmtEur(balanceCents)}
        </span>
      </div>

      <div className="col-span-6 md:col-span-2 text-right">
        {days === null ? (
          <span className="text-xs" style={{ color: "var(--subtle)" }}>
            —
          </span>
        ) : (
          <div className="text-xs leading-tight" style={{ color: isStale ? "var(--warn)" : "var(--muted)" }}>
            {isStale ? "⚠ " : ""}
            {fmtRelativeDays(days, t)}
            <div className="text-[10px]" style={{ color: "var(--subtle)" }}>
              {account.latestBalance?.date}
            </div>
          </div>
        )}
      </div>
    </Link>
  );
}

function Badge({
  children,
  tone,
}: {
  children: React.ReactNode;
  tone: "pos" | "warn" | "info" | "muted";
}) {
  const colors: Record<typeof tone, { bg: string; fg: string }> = {
    pos: { bg: "var(--pos-soft)", fg: "var(--pos)" },
    warn: { bg: "var(--warn-soft)", fg: "var(--warn)" },
    info: { bg: "var(--accent-soft)", fg: "var(--accent)" },
    muted: { bg: "var(--border-soft)", fg: "var(--muted)" },
  };
  const c = colors[tone];
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold"
      style={{ background: c.bg, color: c.fg }}
    >
      {children}
    </span>
  );
}

function liquidityTone(lc: LiquidityCategory): "pos" | "warn" | "info" | "muted" {
  switch (lc) {
    case "LIQUID":
      return "pos";
    case "ANLAGE":
      return "info";
    case "VERBINDLICHKEIT":
      return "warn";
    default:
      return "muted";
  }
}
